import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Chip,
    TextField,
    Button,
    Card,
    CardContent,
    Stack,
    Skeleton,
} from '@mui/material';
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import SendIcon from '@mui/icons-material/Send';
import API from "../config/axios";
import { api_paths } from '../config/api.paths';
import { toastErrorMessage, toastSuccessMessage } from '../utils/utils';
import { clearError, validateData } from "../validators/YupValidations";
import { contactSchema } from '../validators/validatorSchema';

const ContactSection = ({data}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [submitting, setSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [errors, setErrors] = useState({});

    const inputSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            backgroundColor: '#f8fafc',

            '& fieldset': {
                borderColor: '#d6e0f0',
            },

            '&:hover fieldset': {
                borderColor: '#b6c7e2',
            },

            '&.Mui-focused fieldset': {
                borderColor: '#3b82f6', // blue focus
                borderWidth: '2px',
            },
        },

        '& input': {
            color: '#0f172a',
        },

        '& textarea': {
            color: '#0f172a',
        },
    };

    const isLoading = data === undefined;

    const contactInfo = [
        { key: 'email', icon: <MailOutlineIcon /> },
        { key: 'contact', icon: <PhoneInTalkIcon /> },
        { key: 'location', icon: <LocationOnOutlinedIcon /> },
    ];

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        company_name: "",
        message: "",
    });

    const handleSubmit = async(e) => {
        e.preventDefault();

        const isValid = await validateData(contactSchema, formData, setErrors);
        if (!isValid) return;

        try{
            setSubmitting(true);
            const response = await API.post(api_paths.CONTACT.post, formData);

            // toastSuccessMessage("Message sent successfully!");
            setIsSuccess(true);
            // Optionally hide it after 3 seconds
            setTimeout(() => setIsSuccess(false), 3000);
            setFormData({
                name: "",
                email: "",
                company_name: "",
                message: "",
            });
        }catch(error){
            toastErrorMessage("Failed to send message. Please try again later.");
        }finally{
            setSubmitting(false);
        }
    }

    useEffect(() => {
        return () => {
            setFormData({
                name: "",
                email: "",
                company_name: "",
                message: "",
            });

            setErrors({});
        }
    }, []);

    return (
        <Box
            id="contact"
            sx={{
                py: 10,
            }}
        >
            <Container maxWidth="xl">
                {/* MAIN FLEX LAYOUT */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 8,
                        alignItems: 'center',
                        flexDirection: { xs: 'column', md: 'row' },
                    }}
                >
                    {/* LEFT CONTENT */}
                    <Box sx={{ flex: 1, width: '100%' }}>
                        <Chip
                            label={t('pages.home.contact.chip_title')}
                            sx={{
                                backgroundColor: theme.palette.background.chip,
                                color: theme.palette.custom.chipText,
                                fontWeight: 300,
                                fontSize: '1rem',
                                mb: 3,
                                px: 2,
                            }}
                        />

                        <Typography
                            variant="h3"
                            fontWeight="bold"
                            sx={{
                                color: theme.typography.h3?.color || '#3784b4ff',
                                mb: 2,
                                fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.5rem' },
                                lineHeight: 1.2,
                            }}
                        >
                            {isLoading ? (
                                <Skeleton variant="text" height={60} width="80%" />
                            ) : (
                                data?.meta?.title || t('pages.home.contact.headline')
                            )}
                        </Typography>

                        <Typography
                            color="text.secondary"
                            sx={{ mb: 6, fontSize: '1.1rem', maxWidth: 520 }}
                        >
                            {isLoading ? (
                                <Skeleton variant="text" height={30} width="100%" />
                            ) : (
                                data?.meta?.description || t('pages.home.contact.subheadline')
                            )}
                        </Typography>

                        <Stack spacing={4}>
                            {contactInfo.map(({ key, icon }) => (
                                <Box
                                    key={key}
                                    sx={{ display: 'flex', alignItems: 'center', gap: 3 }}
                                >
                                    <Box
                                        sx={{
                                            width: 50,
                                            height: 50,
                                            borderRadius: '12px',
                                            background: theme.palette.custom.serviceIconBg,
                                            color: theme.palette.custom.serviceIconColor,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                        }}
                                    >
                                        {React.cloneElement(icon, { fontSize: 'medium' })}
                                    </Box>

                                    <Box>
                                        <Typography variant="body2" color="text.secondary">
                                            {isLoading ? (
                                                <Skeleton variant="text" height={20} width={120} />
                                            ) : (
                                                t(`pages.home.contact.info.${key}.title`)
                                            )}
                                        </Typography>
                                        <Typography variant="body2" color="text.primary" fontWeight="medium">
                                            {isLoading ? (
                                                <Skeleton variant="text" height={20} width={120} />
                                            ) : (
                                                data?.meta?.[key] || t(`pages.home.contact.info.${key}.value`)
                                            )}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Stack>
                    </Box>

                    {/* RIGHT FORM */}
                    <Box sx={{ flex: 1, width: '100%' }}>
                        <Card
                            sx={{
                                borderRadius: '12px',
                                boxShadow:
                                    theme.palette.custom.cardShadowLarge ||
                                    '0 10px 40px rgba(0,0,0,0.08)',
                                border: '1px solid',
                                borderColor: 'divider',
                                p: { xs: 2, md: 2 },
                            }}
                        >
                            <CardContent>
                                <Typography variant="h5" fontWeight="medium" sx={{ mb: 4 }}>
                                    {t('pages.home.contact.form.title')}
                                </Typography>

                                <Box
                                    component="form"
                                    onSubmit={handleSubmit}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                        gap: 3,
                                    }}
                                >
                                    <Box>
                                        <Typography fontWeight={500} sx={{ mb: 1 }}>
                                            {t('pages.home.contact.form.name')}
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            name="name"
                                            placeholder="John Doe"
                                            sx={inputSx}
                                            value={formData.name}
                                            onChange={(e) => {
                                                setFormData({ ...formData, name: e.target.value });
                                                errors.name && clearError(setErrors, 'name');
                                            }}
                                        />
                                        {errors.name && (
                                            <Typography variant="caption" color="error" sx={{ mt: 0.5, fontSize: '1rem', fontWeight: 400 }}>
                                                {errors.name.message}
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box>
                                        <Typography fontWeight={500} sx={{ mb: 1 }}>
                                            {t('pages.home.contact.form.email')}
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            name="email"
                                            type='email'
                                            placeholder="john@example.com"
                                            sx={inputSx}
                                            value={formData.email}
                                            onChange={(e) => {
                                                setFormData({ ...formData, email: e.target.value });
                                                errors.email && clearError(setErrors, 'email');
                                            }} 
                                        />
                                        {errors.email && (
                                            <Typography variant="caption" color="error" sx={{ mt: 0.5, fontSize: '1rem', fontWeight: 400 }}>
                                                {errors.email.message}
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box sx={{ gridColumn: '1 / -1' }}>
                                        <Typography fontWeight={500} sx={{ mb: 1 }}>
                                            {t('pages.home.contact.form.company')}
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            name="company_name"
                                            placeholder="Your Company"
                                            sx={inputSx} 
                                            value={formData.company_name}
                                            onChange={(e) => {
                                                setFormData({ ...formData, company_name: e.target.value });
                                            }}    
                                        />
                                    </Box>

                                    <Box sx={{ gridColumn: '1 / -1' }}>
                                        <Typography fontWeight={500} sx={{ mb: 1 }}>
                                            {t('pages.home.contact.form.message')}
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            name="message"
                                            multiline
                                            rows={5}
                                            placeholder="Tell us about your project..."
                                            sx={inputSx} 
                                            value={formData.message}
                                            onChange={(e) => {
                                                setFormData({ ...formData, message: e.target.value });
                                                errors.message && clearError(setErrors, 'message');
                                            }}
                                        />
                                        {errors.message && (
                                            <Typography variant="caption" color="error" sx={{ mt: 0.5, fontSize: '1rem', fontWeight: 400 }}>
                                                {errors.message.message}
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box sx={{ gridColumn: '1 / -1' }}>
                                        <Button
                                            fullWidth
                                            type='submit'
                                            variant="contained"
                                            size="large"
                                            disabled={submitting}
                                            endIcon={<SendIcon />}
                                            sx={{
                                                mt: 2,
                                                py: 2,
                                                borderRadius: '12px',
                                                background: theme.palette.custom.buttonGradient,
                                                fontSize: '1.1rem',
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            {submitting ? t('pages.home.contact.form.submitting') || 'Sending...' : t('pages.home.contact.form.submit')}
                                        </Button>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                </Box>
            </Container>
            <AnimatePresence>
                {isSuccess && (
                <motion.div
                    // Full-page success message
                    style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 9999,
                    }}
                    // Entry animation
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    // Exit animation
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <motion.div
                    style={{
                        padding: "40px 60px",
                        backgroundColor: "#4CAF50",
                        color: "white",
                        borderRadius: "12px",
                        boxShadow: "0 20px 50px rgba(0,0,0,0.3)",
                        textAlign: "center",
                    }}
                    initial={{ scale: 0.5, y: -50 }}
                    animate={{ scale: 1, y: 0 }}
                    exit={{ scale: 0.5, y: -50 }}
                    transition={{ type: "spring", damping: 20, stiffness: 300 }}
                    >
                        <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                            Success!
                        </Typography>
                        <Typography variant="body1">
                            Your message has been sent successfully.
                        </Typography>
                    </motion.div>
                </motion.div>
                )}
            </AnimatePresence>
        </Box>
    );
};

export default ContactSection;
